import { COLOR_APPLICATIONS } from '../../routes/color-constants'
import { AppTable, Field } from '../../components/table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Actions, Pager, Sort } from '../../components/table/types'
import seeIcon from '../../assets/table-icons/see-icon.svg'
import { useTranslation } from 'react-i18next'
import { BoolQueryParam, Query, QueryParam, SortParam } from '../../common/api/Query'
import { useNavigate } from 'react-router-dom'
import { URL_PATIENT_REQUESTS } from '../../routes/routes-constants'
import { getAuthContainer } from '../../container/auth-modules'
import { AuthService } from '../../modules/auth/services/AuthService'
import { AUTH_SERVICE_KEY } from '../../modules/auth'
import { getPatientContainer } from 'container/patient-modules'
import { PatientService } from 'modules/patient/services/PatientService'
import { PATIENT_SERVICE_KEY } from 'modules/patient'
import { Box, TextField, CircularProgress } from '@mui/material'
import { Permission } from 'common/enums/Permissions'
import { PatientQuery } from 'modules/patient/models/Patient'

const authService = getAuthContainer().get<AuthService>(AUTH_SERVICE_KEY)
const patientService = getPatientContainer().get<PatientService>(PATIENT_SERVICE_KEY)

type TableProps = { origin?: string }

export const Table = (props: TableProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const loggedUser = authService.get()
  const [items, setItems] = useState<any[]>([])
  const [allItems, setAllItems] = useState<any[]>([])
  const [page, setPage] = useState<number>(0) // 0-based interno
  const [count, setCount] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(10)
  const [searchValue, setSearchValue] = useState<string>('')
  const [loading, setLoading] = useState(false)

  // ✅ Debounce search (300ms)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchValue), 300)
    return () => clearTimeout(timeout)
  }, [searchValue])

  // ✅ Filtrado optimizado
const filteredItems = useMemo(() => {
  let result = allItems
  
  // 1. Filtrar primero
  if (debouncedSearch?.trim()) {
    const term = debouncedSearch.toUpperCase()
    result = allItems.filter((i) => {
      const idPrefix = i.id.substring(0, i.id.indexOf('-')).toUpperCase()
      const searchableId = `${i.firstName.toUpperCase()}-${idPrefix}`
      const fullName = `${i.firstName} ${i.lastName}`.toUpperCase()
      const nhc = i.nhc.toUpperCase()
      
      return searchableId.includes(term) || fullName.includes(term) || nhc.includes(term)
    })
  }
  
  // 2. Ordenar por firstName (A-Z)
  return result.sort((a, b) => 
    a.firstName.localeCompare(b.firstName)
  )
}, [allItems, debouncedSearch])

  // ✅ Paginación local
  const paginatedItems = useMemo(() => {
    const start = page * itemsPerPage
    return filteredItems.slice(start, start + itemsPerPage)
  }, [filteredItems, page, itemsPerPage])

  // ✅ Batch state updates
  useEffect(() => {
    setItems(paginatedItems)
    setCount(filteredItems.length)
  }, [paginatedItems, filteredItems.length])

  // ✅ FIXED: Handlers estables con 1-based/0-based conversion
  const handlePaginationChange = useCallback((event: unknown, value: number) => {
    setPage(value) // AppTable pasa 1,2,3... → convierte a 0,1,2...
  }, [])

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🚀 Rows change to:', event.target.value) // DEBUG
    const newSize = Number.parseInt(event.target.value)
    if (!Number.isNaN(newSize)) {
      setItemsPerPage(newSize)
      setPage(0)
    }
  }, [])

  // ✅ FIXED: Pager estable con page 1-based para AppTable
  const pager: Pager = useMemo(() => ({
    page: page , // 0→1, 1→2... (AppTable espera 1-based)
    count,
    handleChangePage: handlePaginationChange,
    rowsPerPage: itemsPerPage,
    handleChangeRowsPerPage,
  }), [page, count, itemsPerPage, handlePaginationChange, handleChangeRowsPerPage])

  // ✅ Load data
  const getPatients = useCallback(async () => {
    setLoading(true)
    try {
      let queries: QueryParam<PatientQuery>[] = []
      
      if (!authService.userCan(Permission.seeAllPatients)) {
        queries.push(new QueryParam('clients', loggedUser.clinicsList))
      }
      queries.push(new QueryParam('show', new BoolQueryParam(true)))
      
      // Descomenta para backend search (>5k items):
      // if (debouncedSearch?.trim()) queries.push(new QueryParam('search', debouncedSearch))

      const q = new Query({ query: queries })
      const all = await patientService.getAllPatients(q).toPromise()
      const resultArray = [...(all?.items || [])]
      
      const uniqueItems = filtrarPersonas(resultArray)
      setAllItems(uniqueItems)
      setCount(uniqueItems.length)
    } catch (error) {
      console.error('Error loading patients:', error)
    } finally {
      setLoading(false)
    }
  }, [loggedUser.clinicID, debouncedSearch])

  useEffect(() => {
    getPatients()
  }, [getPatients])

  // ✅ Deduplicar por nombre
  function filtrarPersonas(personas: any[]): any[] {
    const personasFiltradas: { [key: string]: any } = {}


    personas.forEach((persona) => {
      if (!personasFiltradas[persona.firstName + persona.lastName]) {
        personasFiltradas[persona.firstName + persona.lastName] = persona
        personasFiltradas[persona.firstName + persona.lastName].findIDs = [persona.id]
      } else {
        personasFiltradas[persona.firstName + persona.lastName].findIDs.push(persona.id)
      }
    })


    return Object.values(personasFiltradas)
  }

  const fields: Field<any>[] = [
    {
      searchable: true,
      label: t('ID'),
      name: 'id',
      sortable: false,
      renderFunc: (field, item) => `${item.firstName}-${item.id.substring(0, item.id.indexOf('-'))}`,
    },
    {
      searchable: true,
      label: t('firstNameAndLastName'),
      name: 'patientName',
      sortable: false,
      renderFunc: (field, item) => `${item.firstName} ${item.lastName}`,
    },
  ]


  const viewRequests = (c2r: any) => navigate(URL_PATIENT_REQUESTS.replace(':id', `${c2r.id}`),{state:{list: c2r.findIDs}})

  const actions: Actions<any> = {
    actionsColumn: t('Actions'),
    items: [{ handler: viewRequests, icon: seeIcon, label: t('Show') }],
  }

  const sortable: Sort<any> = {
    name: 'firstName',
    direction: 'desc',
    handleSort: () => {},
  }

  return (
    <div style={{ marginBottom: '2%' }}>
      <Box marginBottom="5px">
        <Box display="flex" flexWrap="wrap" alignItems="center">
          <Box mr={2} mb={2}>
            <TextField
              label={t('search')}
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchValue}
              variant="outlined"
              size="small"
            />
          </Box>
          {loading && <CircularProgress size={20} />}
        </Box>
      </Box>
      <AppTable
        styleHeader={{ color: COLOR_APPLICATIONS }}
        actions={actions}
        fields={fields}
        items={items}
        rowKeyField="id"
        pager={pager}
        sort={sortable}
        origin={props.origin}
      />
    </div>
  )
}
